'use client';

/* ============================================================================
 * v2 Form Primitives
 * ============================================================================
 * Restrained, Linear-grade form atoms used by RequestDemoV2 and ContactUsV2.
 * Built on the v2 design tokens (border, primary, foreground-muted, ...).
 *
 * User-facing strings (aria labels, file picker prompts, default file hint)
 * are localised via the `Forms.Primitives` namespace in messages/<locale>.json.
 * Callers can still override `hint` on FileField when a different size or
 * format constraint is in effect.
 * ========================================================================= */

import React from 'react';
import {useTranslations} from 'next-intl';

const fieldLabel =
  'font-mono text-[11px] tracking-[0.12em] uppercase text-foreground-subtle';

/* ---------------------------------------------------------------------------
 * TextField - text / email / tel / etc.
 * ------------------------------------------------------------------------ */

export function TextField({
  id,
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  required = false,
  autoComplete,
  className = ''
}) {
  return (
    <div className={`space-y-2 ${className}`}>
      <label htmlFor={id} className={fieldLabel}>
        {label}
        {required && <span className="ml-1 text-foreground-muted">*</span>}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        autoComplete={autoComplete}
        className="h-11 w-full rounded-lg border border-border bg-white px-4 text-[14px] text-foreground transition-colors placeholder:text-foreground-subtle hover:border-border-strong focus:border-primary focus:outline-none"
      />
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * TextArea
 * ------------------------------------------------------------------------ */

export function TextArea({
  id,
  label,
  value,
  onChange,
  placeholder,
  rows = 5,
  required = false,
  className = ''
}) {
  return (
    <div className={`space-y-2 ${className}`}>
      <label htmlFor={id} className={fieldLabel}>
        {label}
        {required && <span className="ml-1 text-foreground-muted">*</span>}
      </label>
      <textarea
        id={id}
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full rounded-lg border border-border bg-white px-4 py-3 text-[14px] leading-relaxed text-foreground transition-colors placeholder:text-foreground-subtle hover:border-border-strong focus:border-primary focus:outline-none resize-y"
      />
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * SelectField - native select with custom chevron
 * ------------------------------------------------------------------------ */

export function SelectField({
  id,
  label,
  value,
  onChange,
  options,
  placeholder,
  required = false,
  className = ''
}) {
  return (
    <div className={`space-y-2 ${className}`}>
      <label htmlFor={id} className={fieldLabel}>
        {label}
        {required && <span className="ml-1 text-foreground-muted">*</span>}
      </label>
      <div className="relative">
        <select
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          className="h-11 w-full appearance-none rounded-lg border border-border bg-white px-4 pr-9 text-[14px] text-foreground transition-colors hover:border-border-strong focus:border-primary focus:outline-none"
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((opt) => {
            const optValue = typeof opt === 'string' ? opt : opt.value;
            const optLabel = typeof opt === 'string' ? opt : opt.label;
            return (
              <option key={optValue} value={optValue}>
                {optLabel}
              </option>
            );
          })}
        </select>
        <svg
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 size-3 text-foreground-subtle"
          viewBox="0 0 12 8"
          fill="none"
          aria-hidden
        >
          <path
            d="M1 1.5L6 6.5L11 1.5"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * NumberStepper
 * ------------------------------------------------------------------------ */

export function NumberStepper({
  id,
  label,
  value,
  onChange,
  min = 0,
  max = Infinity,
  className = ''
}) {
  const t = useTranslations('Forms.Primitives.NumberStepper');
  const dec = () => onChange(Math.max(min, value - 1));
  const inc = () => onChange(Math.min(max, value + 1));
  const handleInput = (e) => {
    const v = e.target.value;
    if (v === '' || /^\d+$/.test(v)) {
      const next = v === '' ? min : Math.max(min, Math.min(max, parseInt(v, 10)));
      onChange(next);
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <label htmlFor={id} className={fieldLabel}>
        {label}
      </label>
      <div className="grid h-11 w-fit grid-cols-[32px_64px_32px] items-center rounded-lg border border-border bg-white px-1 transition-colors hover:border-border-strong focus-within:border-primary">
        <button
          type="button"
          onClick={dec}
          aria-label={t('decrease')}
          className="flex size-8 items-center justify-center rounded-md text-foreground-muted transition-colors hover:bg-background-subtle hover:text-foreground"
        >
          <svg viewBox="0 0 12 2" className="size-3" fill="none" aria-hidden>
            <path
              d="M1 1H11"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <input
          id={id}
          type="text"
          inputMode="numeric"
          value={value}
          onChange={handleInput}
          className="w-full bg-transparent p-0 text-center text-[14px] tabular-nums text-foreground focus:outline-none"
        />
        <button
          type="button"
          onClick={inc}
          aria-label={t('increase')}
          className="flex size-8 items-center justify-center rounded-md text-foreground-muted transition-colors hover:bg-background-subtle hover:text-foreground"
        >
          <svg viewBox="0 0 12 12" className="size-3" fill="none" aria-hidden>
            <path
              d="M6 1V11M1 6H11"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * RadioCardGroup - icon + label cards in a grid (used for property type pick)
 * ------------------------------------------------------------------------ */

export function RadioCardGroup({label, value, onChange, options, className = ''}) {
  return (
    <div className={`space-y-3 ${className}`}>
      <p className={fieldLabel}>{label}</p>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
        {options.map((opt) => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              aria-pressed={active}
              className={
                active
                  ? 'flex flex-col items-start gap-3 rounded-lg border border-primary bg-brand-50 px-4 py-3.5 text-left transition-colors'
                  : 'flex flex-col items-start gap-3 rounded-lg border border-border bg-white px-4 py-3.5 text-left transition-colors hover:border-border-strong'
              }
            >
              <span
                className={
                  active
                    ? 'text-primary'
                    : 'text-foreground-subtle transition-colors group-hover:text-foreground'
                }
              >
                {opt.icon}
              </span>
              <span
                className={
                  active
                    ? 'text-[13.5px] font-medium text-primary'
                    : 'text-[13.5px] font-medium text-foreground'
                }
              >
                {opt.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * FileField — multi-file picker with drag-and-drop, file chip list, and
 * remove buttons. Returns native File objects via `onChange(files: File[])`.
 *
 * The component is intentionally controlled and stateless: callers own the
 * `files` array and decide how to encode + ship them on submit. The legacy
 * Support form sent `File` objects through `JSON.stringify`, which silently
 * stripped the bytes (File serialises to `{}`); v2 callers should base64
 * the contents before sending so the backend can actually consume them.
 * ------------------------------------------------------------------------ */

export function FileField({
  id,
  label,
  files,
  onChange,
  accept,
  hint,
  maxFiles = 5,
  className = ''
}) {
  const t = useTranslations('Forms.Primitives.FileField');
  const inputId = id || 'file-upload';
  const hintText = hint ?? t('defaultHint');

  const handleAdd = (incoming) => {
    if (!incoming || incoming.length === 0) return;
    const list = Array.from(incoming);
    const next = [...files, ...list].slice(0, maxFiles);
    onChange(next);
  };

  const handleRemove = (idx) => {
    onChange(files.filter((_, i) => i !== idx));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleAdd(e.dataTransfer.files);
  };

  const formatSize = (size) =>
    size > 1024 * 1024
      ? `${(size / (1024 * 1024)).toFixed(2)} MB`
      : `${(size / 1024).toFixed(1)} KB`;

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label htmlFor={inputId} className={fieldLabel}>
          {label}
        </label>
      )}
      <label
        htmlFor={inputId}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-white px-6 py-8 text-center transition-colors hover:border-border-strong hover:bg-background-subtle"
      >
        <svg
          viewBox="0 0 24 24"
          className="size-6 text-foreground-subtle"
          fill="none"
          aria-hidden
        >
          <path
            d="M12 16V4m0 0L7 9m5-5l5 5M5 16v3a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-3"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <p className="text-[13.5px] text-foreground">
          <span className="text-primary">{t('clickToUpload')}</span>{' '}
          <span className="text-foreground-subtle">{t('orDragAndDrop')}</span>
        </p>
        <p className="text-[11.5px] text-foreground-subtle">{hintText}</p>
        <input
          id={inputId}
          type="file"
          multiple
          accept={accept}
          onChange={(e) => {
            handleAdd(e.target.files);
            e.target.value = '';
          }}
          hidden
        />
      </label>
      {files.length > 0 && (
        <ul className="space-y-2 pt-1">
          {files.map((file, idx) => (
            <li
              key={`${file.name}-${idx}`}
              className="flex items-center justify-between gap-3 rounded-lg border border-border bg-white px-3 py-2"
            >
              <div className="flex min-w-0 items-center gap-3">
                <span className="inline-flex size-7 items-center justify-center rounded-md bg-brand-50 text-primary">
                  <svg viewBox="0 0 16 16" className="size-3.5" fill="none" aria-hidden>
                    <path
                      d="M3 1.5h6.5L13 5v9.5H3V1.5Z"
                      stroke="currentColor"
                      strokeWidth="1.4"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9 1.5V5h4"
                      stroke="currentColor"
                      strokeWidth="1.4"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <div className="min-w-0">
                  <p className="truncate text-[13px] text-foreground">{file.name}</p>
                  <p className="text-[11px] text-foreground-subtle">
                    {formatSize(file.size)}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleRemove(idx)}
                aria-label={t('remove', {name: file.name})}
                className="flex size-7 items-center justify-center rounded-md text-foreground-subtle transition-colors hover:bg-background-subtle hover:text-foreground"
              >
                <svg viewBox="0 0 12 12" className="size-3" fill="none" aria-hidden>
                  <path
                    d="M2 2L10 10M10 2L2 10"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * StepIndicator - small horizontal numbered step bar
 * ------------------------------------------------------------------------ */

export function StepIndicator({steps, current}) {
  return (
    <ol className="flex items-center gap-3">
      {steps.map((label, i) => {
        const idx = i + 1;
        const done = idx < current;
        const active = idx === current;
        return (
          <li key={label} className="flex items-center gap-3">
            <span
              className={
                done
                  ? 'inline-flex size-6 items-center justify-center rounded-full bg-primary text-white'
                  : active
                  ? 'inline-flex size-6 items-center justify-center rounded-full border border-primary bg-brand-50 text-primary'
                  : 'inline-flex size-6 items-center justify-center rounded-full border border-border text-foreground-subtle'
              }
            >
              {done ? (
                <svg viewBox="0 0 10 10" className="size-3" fill="none" aria-hidden>
                  <path
                    d="M2 5.5L4 7.5L8 3"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <span className="font-mono text-[11px] font-medium">{idx}</span>
              )}
            </span>
            <span
              className={
                active
                  ? 'font-mono text-[11px] tracking-[0.12em] uppercase text-foreground'
                  : 'font-mono text-[11px] tracking-[0.12em] uppercase text-foreground-subtle'
              }
            >
              {label}
            </span>
            {idx < steps.length && (
              <span className="ml-1 hidden h-px w-8 bg-border md:block" />
            )}
          </li>
        );
      })}
    </ol>
  );
}

/* ---------------------------------------------------------------------------
 * FormButton - primary / secondary buttons sized for form rows
 * ------------------------------------------------------------------------ */

export function FormButton({
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  disabled = false,
  fullWidth = false,
  arrow = 'right'
}) {
  const base =
    'inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg text-[13.5px] font-medium transition-all duration-200';

  const styles =
    variant === 'primary'
      ? 'bg-primary text-white hover:bg-primary-hover hover:-translate-y-px shadow-[0_1px_0_0_rgba(255,255,255,0.18)_inset,0_6px_16px_-8px_rgba(22,67,224,0.55)] hover:shadow-[0_1px_0_0_rgba(255,255,255,0.22)_inset,0_12px_24px_-10px_rgba(22,67,224,0.7)] disabled:bg-gray-300 disabled:text-white disabled:shadow-none disabled:cursor-not-allowed disabled:hover:translate-y-0'
      : 'bg-white border border-border text-foreground hover:border-border-strong disabled:opacity-50 disabled:cursor-not-allowed';

  const widthCls = fullWidth ? 'w-full' : '';

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${base} ${styles} ${widthCls}`}
    >
      {arrow === 'left' && <ArrowLeft />}
      <span>{children}</span>
      {arrow === 'right' && <ArrowRight />}
    </button>
  );
}

function ArrowRight() {
  return (
    <svg viewBox="0 0 14 10" className="size-3.5" fill="none" aria-hidden>
      <path
        d="M9 1L13 5L9 9M13 5H1"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowLeft() {
  return (
    <svg viewBox="0 0 14 10" className="size-3.5" fill="none" aria-hidden>
      <path
        d="M5 1L1 5L5 9M1 5H13"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
